import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { homebaseQuizState, QUIZ_VIEW } from '~/types/homebaseQuiz.types'

import { logout } from '~/auth/slices/auth.slice'
import { RootState } from '~/redux/store'
import { AdvancedQuizQuestion, MappingQuizData, MappingQuizUserAnswer, QuizQuestion } from '~/shared/types/Quiz.types'

const INITIAL_ADVANCED_SUBJECTS = {
  articles: [],
  question: '',
}

const initialState: homebaseQuizState = {
  quizState: [],
  quizSubject: undefined,
  advancedSubjects: INITIAL_ADVANCED_SUBJECTS,
  quizView: QUIZ_VIEW.INITIAL,
  mappingQuizData: undefined,
  completedQuizes: 0,
}
const resetState: homebaseQuizState = {
  quizState: [],
  quizSubject: undefined,
  advancedSubjects: INITIAL_ADVANCED_SUBJECTS,
  mappingQuizData: undefined,
  completedQuizes: 0,
}

const slice = createSlice({
  name: 'homebaseQuiz',
  initialState,
  reducers: {
    setQuizState: (state, action: PayloadAction<MappingQuizUserAnswer[] | []>) => ({
      ...state,
      quizState: action.payload,
    }),
    addQuizState: (state, action: PayloadAction<MappingQuizUserAnswer>) => ({
      ...state,
      quizState: [...state.quizState, action.payload],
    }),
    setQuizSubject: (state, action: PayloadAction<QuizQuestion | undefined>) => ({
      ...state,
      quizSubject: action.payload,
    }),
    setAdvancedSubjects: (state, action: PayloadAction<AdvancedQuizQuestion>) => ({
      ...state,
      advancedSubjects: action.payload,
    }),
    setQuizView: (state, action: PayloadAction<QUIZ_VIEW>) => ({
      ...state,
      quizView: action.payload,
    }),
    setMappingQuizData: (state, action: PayloadAction<MappingQuizData | undefined>) => ({
      ...state,
      mappingQuizData: action.payload,
    }),
    setCompletedQuizeCount: (state) => ({
      ...state,
      completedQuizes: state.completedQuizes + 1,
    }),
    addMissingTopicsToMappingQuizData: (state, action: PayloadAction<string[]>) => {
      if (!state.mappingQuizData) {
        return {
          ...state,
        }
      } else {
        return {
          ...state,
          mappingQuizData: {
            ...state.mappingQuizData,
            missingTopics: action.payload,
          },
        }
      }
    },
    resetHomebaseQuizState: (state) => ({
      ...state,
      ...resetState,
    }),
    resetAllHomebaseQuizState: () => ({
      ...initialState,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)
  },
})

export const selectQuizState = (state: RootState): MappingQuizUserAnswer[] => state.homebaseQuiz.quizState
export const selectQuizSubject = (state: RootState): QuizQuestion | undefined => state.homebaseQuiz.quizSubject
export const selectAdvancedSubjects = (state: RootState): AdvancedQuizQuestion => state.homebaseQuiz.advancedSubjects
export const selectCompletedQuizCount = (state: RootState): number => state.homebaseQuiz.completedQuizes
export const selectQuizView = (state: RootState): QUIZ_VIEW | undefined => state.homebaseQuiz.quizView
export const selectMappingQuizData = (state: RootState): MappingQuizData | undefined =>
  state.homebaseQuiz.mappingQuizData

export const {
  setQuizState: setQuizStateActionCreator,
  addQuizState: addQuizStateActionCreator,
  setQuizSubject: setQuizSubjectActionCreator,
  setAdvancedSubjects: setAdvancedSubjectsActionCreator,
  setQuizView: setQuizViewActionCreator,
  setMappingQuizData: setMappingQuizDataActionCreator,
  setCompletedQuizeCount: setCompletedQuizCountActionCreator,
  addMissingTopicsToMappingQuizData: addMissingTopicsToMappingQuizDataActionCreator,
  resetHomebaseQuizState: resetHomebaseQuizStateActionCreator,
  resetAllHomebaseQuizState: resetAllHomebaseQuizStateActionCreator,
} = slice.actions

export default slice.reducer
